import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Briefcase,
  ExternalLink,
  Loader2,
  X,
  Calendar,
  Hash,
  Users,
} from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

interface Workspace {
  id: string;
  name: string;
  kind: string;
  status: string;
  created_at: string;
  description?: string | null;
}

interface WorkspaceCollaborator {
  id: string;
  user_id: string;
  can_edit: boolean;
  user?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
}

interface OrgWorkspacesPanelProps {
  orgId: string;
}

async function fetchWorkspaces(orgId: string): Promise<Workspace[]> {
  const res = await apiFetch(`/api/workspaces?organization_id=${orgId}`);
  if (!res.ok) throw new Error(`Failed to load workspaces: ${res.status}`);
  const json = await res.json();
  const data = Array.isArray(json) ? json : (json.data ?? []);
  return data.filter(
    (ws: Workspace) => ws.status !== 'archived'
  ) as Workspace[];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Slide-out panel ───────────────────────────────────────────────────────────

function SlideOut({
  workspace,
  onClose,
  onNavigate,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onNavigate: (id: string) => void;
}) {
  const [members, setMembers] = useState<WorkspaceCollaborator[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    if (workspace) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [workspace, onClose]);

  useEffect(() => {
    let cancelled = false;
    const wsId = workspace?.id ?? null;
    async function load() {
      if (!wsId) {
        if (!cancelled) setMembers([]);
        return;
      }
      if (!cancelled) setLoadingMembers(true);
      try {
        const r = await apiFetch(`/api/workspaces/${wsId}/collaborators`);
        const data: WorkspaceCollaborator[] = r.ok ? await r.json() : [];
        if (!cancelled) setMembers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [workspace?.id]);

  const isVisible = !!workspace;

  const kindBadge = (kind: string) => {
    switch (kind) {
      case 'jurisdiction':
        return (
          <Badge variant="default" className="text-[10px]">
            Jurisdiction
          </Badge>
        );
      case 'themed':
        return (
          <Badge variant="secondary" className="text-[10px]">
            Themed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-[10px]">
            Custom
          </Badge>
        );
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      draft: 'bg-yellow-100 text-yellow-700',
      archived: 'bg-gray-100 text-gray-500',
    };
    return (
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-black/20 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <div
        ref={ref}
        className={`fixed right-0 top-0 z-40 flex h-full w-[440px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {workspace && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-gray-900">
                    {workspace.name}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {kindBadge(workspace.kind)}
                    {statusBadge(workspace.status)}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 mt-0.5 shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {workspace.description && (
                <p className="text-sm text-gray-600 leading-relaxed">
                  {workspace.description}
                </p>
              )}

              {/* Metadata */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 divide-y divide-gray-100">
                {[
                  {
                    icon: Hash,
                    label: 'Workspace ID',
                    value: (
                      <span className="font-mono text-xs text-gray-400 break-all">
                        {workspace.id}
                      </span>
                    ),
                  },
                  {
                    icon: Calendar,
                    label: 'Created',
                    value: formatDate(workspace.created_at),
                  },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3 px-4 py-3">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                        {label}
                      </p>
                      <div className="mt-0.5 text-sm text-gray-700">
                        {value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Collaborators */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-400" />
                  <p className="text-sm font-semibold text-gray-700">
                    Collaborators
                  </p>
                </div>
                {loadingMembers ? (
                  <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-gray-400">No collaborators yet.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map((m) => {
                      const name = m.user
                        ? `${m.user.first_name ?? ''} ${m.user.last_name ?? ''}`.trim() ||
                          m.user.email
                        : m.user_id;
                      return (
                        <div
                          key={m.id}
                          className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold uppercase text-blue-700">
                              {m.user?.first_name?.[0] ??
                                m.user?.email?.[0] ??
                                '?'}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-800">
                                {name}
                              </p>
                              {m.user?.email && (
                                <p className="text-xs text-gray-400">
                                  {m.user.email}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                            {m.can_edit ? 'Editor' : 'Viewer'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer action */}
            <div className="border-t border-gray-100 px-6 py-4">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => onNavigate(workspace.id)}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Go to Workspace
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export const OrgWorkspacesPanel: React.FC<OrgWorkspacesPanelProps> = ({
  orgId,
}) => {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWs, setSelectedWs] = useState<Workspace | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWorkspaces(orgId);
      setWorkspaces(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleNavigate = (id: string) => {
    setSelectedWs(null);
    navigate(`/workspaces/${id}`);
  };

  const kindBadge = (kind: string) => {
    switch (kind) {
      case 'jurisdiction':
        return (
          <Badge variant="default" className="text-xs">
            Jurisdiction
          </Badge>
        );
      case 'themed':
        return (
          <Badge variant="secondary" className="text-xs">
            Themed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-xs">
            Custom
          </Badge>
        );
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Workspaces
          </CardTitle>
          <CardDescription>
            Workspaces belonging to this organization.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {error && (
            <div className="border-b border-red-200 bg-red-50 px-6 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading workspaces…
            </div>
          ) : workspaces.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No workspaces found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workspaces.map((ws) => (
                  <TableRow
                    key={ws.id}
                    className={`cursor-pointer transition-colors ${
                      selectedWs?.id === ws.id
                        ? 'bg-blue-50'
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedWs(ws)}
                  >
                    <TableCell>
                      <div>
                        <span className="font-medium">{ws.name}</span>
                        {ws.description && (
                          <p className="text-xs text-gray-400 truncate max-w-xs">
                            {ws.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{kindBadge(ws.kind)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(ws.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Slide-out */}
      <SlideOut
        workspace={selectedWs}
        onClose={() => setSelectedWs(null)}
        onNavigate={handleNavigate}
      />
    </>
  );
};
